import { ExclamationCircleIcon } from "@heroicons/react/20/solid";
import clsx from "clsx";
import type { FocusEvent, ForwardedRef, InputHTMLAttributes } from "react";
import { forwardRef, useEffect, useState } from "react";
import type { Control } from "react-hook-form";
import { useController } from "react-hook-form";

import FormInput from "~/components/formElements/FormInput";
import type { FormInputBaseVariants } from "~/components/formElements/formInputBaseVariant.ts";
import { InputPlain } from "~/components/formElements/Input.tsx";
import Label from "~/components/formElements/Label/Label";

type SharedProps =
	Omit<InputHTMLAttributes<HTMLInputElement>, "onChange" | "value">
	& FormInputBaseVariants & {
	minValue?: number;
	maxValue?: number;
	decimalPlaces?: number;
	className?: string;
	errorIconOnly?: boolean;
	name: string;
	label?: string;
	error?: string;
}

type DecimalInputPlainProps = SharedProps & {
	value: number | null;
	onChange: (value: number | null) => void;
}

export const DecimalInputPlain = forwardRef<HTMLInputElement, DecimalInputPlainProps>(
	(
		{
			className,
			value,
			onBlur,
			onChange,
			label,
			error,
			errorIconOnly,
			name,
			minValue,
			maxValue,
			decimalPlaces = 4,
			...props
		}: DecimalInputPlainProps,
		ref: ForwardedRef<HTMLInputElement>,
	) => {
		const [displayValue, setDisplayValue] = useState<string>(
			value !== null ? value.toString().replace(".", ",") : ""
		);

		// Sync display when value prop changes externally
		useEffect(() => {
			const newDisplayValue = value !== null ? value.toString().replace(".", ",") : "";
			// Only update if the numeric values differ to avoid cursor jumping
			const currentNumeric = parseFloat(displayValue.replace(",", "."));
			if (value !== currentNumeric || (value === null && displayValue !== "")) {
				setDisplayValue(newDisplayValue);
			}
		}, [value]);

		function handleFocus(e: React.FocusEvent<HTMLInputElement>) {
			e.target.select();
		}

		function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
			const inputValue = e.target.value;

			// Only allow digits, comma, dot, and minus
			if (!/^[-0-9.,]*$/.test(inputValue)) {
				return;
			}

			// Prevent multiple decimal separators
			const separatorCount = (inputValue.match(/[.,]/g) || []).length;
			if (separatorCount > 1) {
				return;
			}

			// Prevent multiple minus signs or minus not at start
			const minusCount = (inputValue.match(/-/g) || []).length;
			if (minusCount > 1 || (minusCount === 1 && !inputValue.startsWith("-"))) {
				return;
			}

			setDisplayValue(inputValue);

			if (inputValue === "" || inputValue === "-") {
				onChange(null);
				return;
			}

			const normalized = inputValue.replace(",", ".");

			// Allow intermediate states without calling onChange
			if (normalized.endsWith(".") || normalized === "-.") {
				return;
			}

			const numericValue = parseFloat(normalized);

			if (isNaN(numericValue)) {
				return;
			}

			// Check decimal places
			const decimalPart = normalized.split(".")[1];
			if (decimalPart && decimalPart.length > decimalPlaces) {
				// Reject input exceeding decimal places
				setDisplayValue(displayValue);
				return;
			}

			if (minValue !== undefined && numericValue < minValue) return;
			if (maxValue !== undefined && numericValue > maxValue) return;

			onChange(numericValue);
		}

		function handleBlur(event: FocusEvent<HTMLInputElement>) {
			// On blur, clean up display and enforce constraints
			if (displayValue === "" || displayValue === "-" || displayValue === "," || displayValue === ".") {
				setDisplayValue("");
				onChange(null);
				onBlur && onBlur(event);
				return;
			}

			const normalized = displayValue.replace(",", ".");
			let numericValue = parseFloat(normalized);

			if (isNaN(numericValue)) {
				setDisplayValue("");
				onChange(null);
				onBlur && onBlur(event);
				return;
			}

			// Enforce min/max constraints
			if (minValue !== undefined && numericValue < minValue) {
				numericValue = minValue;
			} else if (maxValue !== undefined && numericValue > maxValue) {
				numericValue = maxValue;
			}

			// Update both display and value
			setDisplayValue(numericValue.toString().replace(".", ","));
			onChange(numericValue);
			onBlur && onBlur(event);
		}

		return (
			<FormInput className={clsx(props.disabled && "opacity-50")}>
				{label && <Label htmlFor={name}>{label}</Label>}
				<div className="relative rounded-md shadow-sm">
					<InputPlain
						name={name}
						className={className}
						type="text"
						inputMode="decimal"
						value={displayValue}
						onChange={handleChange}
						onBlur={handleBlur}
						onFocus={handleFocus}
						ref={ref}
						{...props}
					/>
					{error && (
						<div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
							<ExclamationCircleIcon className="size-5 text-red-500"
												   aria-hidden="true" />
						</div>
					)}
				</div>
				{error && !errorIconOnly && (
					<p className="mt-2 text-sm text-red-600"
					   id={`${name}-error`}>
						{error}
					</p>
				)}
			</FormInput>
		);
	},
);

DecimalInputPlain.displayName = "DecimalInputPlain";

type DecimalInputProps = SharedProps & {
	control: Control<any>;
	useControllerError?: boolean;
	rules?: any;
}

const DecimalInput: React.FC<DecimalInputProps> = ({
	name,
	control,
	useControllerError,
	error,
	rules,
	...rest
}) => {
	const {
		field,
		fieldState: { error: errorFromRHF },
	} = useController({
		name,
		control,
		rules,
	});

	const errorMessage = useControllerError ? errorFromRHF?.message : error;
	return <DecimalInputPlain {...field} {...rest} error={errorMessage} />;
};

export default DecimalInput;